import {
  Divider,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material'
import { useMemo } from 'react'

import {
  buildFormSubmissionFieldLayout,
  formatFormSubmissionFieldValue,
} from '~/features/preChatForms/components/formSubmissionFields/utils'
import { TFormSubmissionFieldForLayout } from '~/features/preChatForms/models'
import rem from '~/utils/ui'

type TFieldRowProps = {
  field: TFormSubmissionFieldForLayout
  labelColor?: string
  valueColor?: string
  missingValueColor?: string
}

function FormSubmissionFieldRow({
  field,
  labelColor = undefined,
  valueColor = undefined,
  missingValueColor = undefined,
}: TFieldRowProps) {
  const theme = useTheme()
  const { text, isMissing } = formatFormSubmissionFieldValue(field.value)

  return (
    <Stack sx={{ flex: 1, minWidth: 0, py: rem(10) }}>
      <Typography
        fontWeight={theme.typography.fontWeightBold}
        sx={{
          color: labelColor ?? theme.palette.text.secondary,
          fontSize: rem(10),
          letterSpacing: rem(0.6),
          lineHeight: 1.2,
          textTransform: 'uppercase',
        }}
        variant="caption"
      >
        {field.displayName}
      </Typography>
      <Typography
        fontWeight={isMissing ? theme.typography.fontWeightRegular : 500}
        sx={{
          color: isMissing
            ? (missingValueColor ?? theme.palette.text.secondary)
            : (valueColor ?? theme.palette.text.primary),
          lineHeight: 1.35,
          wordBreak: 'break-word',
        }}
        variant="body2"
      >
        {text}
      </Typography>
    </Stack>
  )
}

type TProps = {
  fields: TFormSubmissionFieldForLayout[]
  labelColor?: string
  valueColor?: string
  missingValueColor?: string
  sx?: SxProps<Theme>
}

function FormSubmissionFields({
  fields,
  labelColor = undefined,
  valueColor = undefined,
  missingValueColor = undefined,
  sx = {},
}: TProps) {
  const theme = useTheme()
  const fieldLayout = useMemo(
    () => buildFormSubmissionFieldLayout(fields),
    [fields],
  )

  if (fieldLayout.length === 0) {
    return null
  }

  return (
    <Stack
      divider={<Divider flexItem sx={{ borderColor: theme.palette.divider }} />}
      sx={{ minWidth: 0, ...sx }}
    >
      {fieldLayout.map((item) => {
        if (item.type === 'field') {
          return (
            <FormSubmissionFieldRow
              key={`${item.field.displayName}-${String(item.field.value ?? '')}`}
              field={item.field}
              labelColor={labelColor}
              missingValueColor={missingValueColor}
              valueColor={valueColor}
            />
          )
        }

        const nameRowKey = `${item.firstName.displayName}-${item.lastName.displayName}`

        return (
          <Stack
            key={nameRowKey}
            direction="row"
            divider={
              <Divider
                flexItem
                orientation="vertical"
                sx={{ marginRight: rem(12) }}
              />
            }
          >
            <FormSubmissionFieldRow
              field={item.firstName}
              labelColor={labelColor}
              missingValueColor={missingValueColor}
              valueColor={valueColor}
            />
            <FormSubmissionFieldRow
              field={item.lastName}
              labelColor={labelColor}
              missingValueColor={missingValueColor}
              valueColor={valueColor}
            />
          </Stack>
        )
      })}
    </Stack>
  )
}

export default FormSubmissionFields
